import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { getPermitById } from '@/services/permits.service';
import { useAuth } from '@/contexts/AuthContext';
import { LoadingSpinner, ErrorMessage } from '@/components/ui';
import type { PermitSearchDetailResponse } from '@/types/api.types';

export default function PermitSearchDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const {
    data: searchDetail,
    isLoading,
    isError,
    error,
  } = useQuery<PermitSearchDetailResponse>({
    queryKey: ['permitSearch', id],
    queryFn: () => getPermitById(id!),
    enabled: !!id,
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatConfidence = (score: number) => {
    return `${(score * 100).toFixed(0)}%`;
  };

  const getConfidenceColor = (score: number) => {
    if (score >= 0.8) return 'text-green-600';
    if (score >= 0.5) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'extracted':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-md p-8">
          <LoadingSpinner size="lg" />
          <p className="text-center text-gray-600 mt-4">Loading search details...</p>
        </div>
      </div>
    );
  }

  if (isError || !searchDetail) {
    return (
      <div className="container mx-auto px-4 py-8">
        <ErrorMessage
          message={
            error instanceof Error
              ? error.message
              : 'Failed to load permit search details'
          }
        />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="text-indigo-600 hover:text-indigo-800 font-medium mb-4"
        >
          ← Back
        </button>
        <h1 className="text-3xl font-bold text-gray-900">Permit Search Details</h1>
      </div>

      <div className="space-y-6">
        {/* Search Information */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Search Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Location</p>
              <p className="text-lg font-medium">
                {searchDetail.city}, {searchDetail.state}
              </p>
            </div>
            {searchDetail.municipality_name && (
              <div>
                <p className="text-sm text-gray-500">Municipality</p>
                <p className="text-lg font-medium">{searchDetail.municipality_name}</p>
              </div>
            )}
            <div>
              <p className="text-sm text-gray-500">Job Description</p>
              <p className="text-lg font-medium">{searchDetail.job_description}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Search Date</p>
              <p className="text-lg font-medium">{formatDate(searchDetail.created_at)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Status</p>
              <span
                className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
                  searchDetail.extraction_status
                )}`}
              >
                {searchDetail.extraction_status}
              </span>
            </div>
            <div>
              <p className="text-sm text-gray-500">Search Cost</p>
              <p className="text-lg font-medium">{formatCurrency(searchDetail.search_cost)}</p>
            </div>
          </div>
        </div>

        {/* Fee Information */}
        {searchDetail.total_estimated_fee !== undefined && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Fee Information</h2>
            <div className="space-y-4">
              <div className="flex justify-between items-center py-3 bg-indigo-50 px-4 rounded-md">
                <span className="font-semibold text-gray-900 text-lg">Total Estimated Fee</span>
                <span className="font-bold text-2xl text-indigo-600">
                  {formatCurrency(searchDetail.total_estimated_fee)}
                </span>
              </div>

              {searchDetail.extracted_fees && (
                <div className="space-y-2 pt-4 border-t border-gray-200">
                  {searchDetail.extracted_fees.base_fee !== undefined && (
                    <div className="flex justify-between py-2">
                      <span className="text-gray-600">Base Fee:</span>
                      <span className="font-medium">
                        {formatCurrency(searchDetail.extracted_fees.base_fee)}
                      </span>
                    </div>
                  )}
                  {searchDetail.extracted_fees.state_surcharge !== undefined &&
                    searchDetail.extracted_fees.state_surcharge > 0 && (
                      <div className="flex justify-between py-2">
                        <span className="text-gray-600">State Surcharge:</span>
                        <span className="font-medium">
                          {formatCurrency(searchDetail.extracted_fees.state_surcharge)}
                        </span>
                      </div>
                    )}
                  {searchDetail.extracted_fees.processing_fees !== undefined &&
                    searchDetail.extracted_fees.processing_fees > 0 && (
                      <div className="flex justify-between py-2">
                        <span className="text-gray-600">Processing Fees:</span>
                        <span className="font-medium">
                          {formatCurrency(searchDetail.extracted_fees.processing_fees)}
                        </span>
                      </div>
                    )}
                  {searchDetail.extracted_fees.administrative_overhead !== undefined &&
                    searchDetail.extracted_fees.administrative_overhead > 0 && (
                      <div className="flex justify-between py-2">
                        <span className="text-gray-600">Administrative Overhead:</span>
                        <span className="font-medium">
                          {formatCurrency(searchDetail.extracted_fees.administrative_overhead)}
                        </span>
                      </div>
                    )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Permit Link */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Permit Link</h2>
          {searchDetail.permit_link ? (
            <div className="flex items-center gap-2">
              <span className="text-gray-600">Apply for your permit:</span>
              <a
                href={searchDetail.permit_link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-600 hover:text-indigo-800 hover:underline font-medium"
              >
                {searchDetail.permit_link} →
              </a>
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">
              Our apologies, we were unable to find the permit link for this search.
            </p>
          )}
        </div>

        {/* Confidence Scores - Admin Only */}
        {isAdmin &&
          (searchDetail.extraction_confidence !== undefined ||
            searchDetail.fee_confidence) && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Confidence Scores</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {searchDetail.extraction_confidence !== undefined && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Extraction Confidence</p>
                    <p
                      className={`text-2xl font-bold ${getConfidenceColor(
                        searchDetail.extraction_confidence
                      )}`}
                    >
                      {formatConfidence(searchDetail.extraction_confidence)}
                    </p>
                  </div>
                )}
                {searchDetail.fee_confidence && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Fee Confidence</p>
                    <p className="text-2xl font-bold text-gray-900 capitalize">
                      {searchDetail.fee_confidence}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

        {/* Calculation Steps */}
        {searchDetail.fee_breakdown?.calculation_steps &&
          searchDetail.fee_breakdown.calculation_steps.length > 0 && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Calculation Steps</h2>
              <div className="space-y-4">
                {searchDetail.fee_breakdown.calculation_steps.map((step, idx) => (
                  <div
                    key={idx}
                    className="border-l-4 border-indigo-500 pl-4 py-2 bg-gray-50 rounded-r"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-semibold text-gray-900">
                          Step {step.step}: {step.method}
                        </p>
                        <p className="text-sm text-gray-600 mt-1">{step.description}</p>
                      </div>
                      <span className="font-medium text-indigo-600">
                        {formatCurrency(step.amount)}
                      </span>
                    </div>
                    {step.calculation && (
                      <p className="text-sm text-gray-500 mt-2">
                        <span className="font-medium">Calculation:</span> {step.calculation}
                      </p>
                    )}
                    {step.reasoning && (
                      <p className="text-sm text-gray-500 mt-1">
                        <span className="font-medium">Reasoning:</span> {step.reasoning}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

        {/* Error Message */}
        {searchDetail.error_message && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <span className="text-xl">⚠️</span>
              <div className="flex-1">
                <h4 className="font-semibold text-red-900 mb-2">Error</h4>
                <p className="text-sm text-red-800">{searchDetail.error_message}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
